import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismCard,
  PrismCheckbox,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import SettingsMyotbOtbSelfServiceDataBar from 'lumen/components/settings/myotb/otb-self-service-data-bar';
import hasFeature from 'lumen/helpers/has-feature';

interface SettingsMyotbBenchmarkSummarySignature {
  Args: {
    isInterestedInBenchmark: boolean;
    isSetupComplete?: boolean;
    toggleBenchmarkInterest: (event: Event) => void;
  };
  Blocks: {
    default: [];
  };
}

export default class SettingsMyotbBenchmarkSummary extends Component<SettingsMyotbBenchmarkSummarySignature> {
  @service declare myotbSs: Services['myotb-ss'];

  <template>
    <PrismCard class="myotb-card">
      <:header as |Header|>
        <div class="grid gap-400">
          {{#unless @isSetupComplete}}
            <SettingsMyotbOtbSelfServiceDataBar />
          {{/unless}}
          <Header.title>
            {{t
              (if
                this.myotbSs.showMarketBenchmarkEnabledView
                "settings.my_otb.benchmark.title.enabled"
                "settings.my_otb.benchmark.title"
              )
            }}
          </Header.title>
        </div>
      </:header>

      <:content>
        <div class="myotb-card__content-container flex flex-col gap-400">
          <p class="paragraph-300--regular">{{t
              (if
                this.myotbSs.showMarketBenchmarkEnabledView
                "settings.my_otb.benchmark.body.enabled"
                "settings.my_otb.benchmark.body"
              )
            }}</p>

          {{#if (not (hasFeature "otb.market_benchmark_eligible"))}}
            <PrismBanner
              @bannerMessage={{t
                "settings.my_otb.benchmark.area_not_covered_message.body"
              }}
              @bannerTitle={{t
                "settings.my_otb.benchmark.area_not_covered_message.title"
              }}
            />
          {{/if}}

          {{#unless this.myotbSs.showMarketBenchmarkEnabledView}}
            <div class="myotb-card__content-container__benchmark">
              <PrismCheckbox
                @checked={{@isInterestedInBenchmark}}
                @onChange={{@toggleBenchmarkInterest}}
              >
                {{#if (hasFeature "otb.market_benchmark_eligible")}}
                  {{t "settings.my_otb.benchmark.checkbox_label"}}
                {{else}}
                  {{t
                    "settings.my_otb.benchmark.checkbox_label.benchmark_not_eligible"
                  }}
                {{/if}}
              </PrismCheckbox>
            </div>
          {{/unless}}

          <GeneralNavigationIntercomHelpLink
            @articleLink="en/articles/5195786-market-otb-why-opt-in-to-the-forward-looking-occupancy-benchmark"
            @iconName="link-out"
            @isStandaloneLink={{true}}
            @linkText={{t "settings.my_otb.benchmark.explanation.title"}}
            @type="text-and-icon"
          />
        </div>
      </:content>

      <:footer>
        {{yield}}
      </:footer>
    </PrismCard>
  </template>
}
